import { useState, useEffect } from "react";
import { X, Linkedin, MessageCircle, Mail, Twitter, Copy, Check, Handshake, ArrowRight, Heart } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

/**
 * "Aanbevelen" dialog — a first-step choice modal that appears when the
 * user clicks the sidebar "Aanbevelen" button.
 *
 * We deliberately split the flow in two paths because plenty of happy
 * users don't want to think about affiliate commissions:
 *   1. **Alleen delen** — plain URL to https://jobgrid.nl, no tracking.
 *   2. **Word affiliate** — invokes `onOptInAffiliate` which takes the
 *      user to the affiliate signup flow (existing).
 *
 * The plain-share step reuses the exact same layout as the affiliate
 * share modal (WhatsApp/LinkedIn/X/email/copy) so muscle-memory carries
 * over between the two paths.
 */
export default function RecommendDialog({ open, onClose, isAffiliate = false, onOptInAffiliate, plainShareUrl, initialStep = "choose" }) {
  const { t } = useT();
  // "choose" → initial two-way branch. "share" → the actual share sheet.
  const [step, setStep] = useState(initialStep);
  const [copied, setCopied] = useState(false);
  // Lazy-fetched from /auth/me/ref-code so we can credit the SENDER with
  // +14 trial days when the recipient converts to a paid pack.
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/auth/me/ref-code")
      .then((r) => setRefCode(r.data?.ref_code || ""))
      .catch(() => { /* fall back to no ref — link still works, just no reward */ });
  }, [open]);

  if (!open) return null;

  // Compose the shareable URL: `?src=friend&ref=<code>` — recipient's
  // register-flow captures both so `_activate_pack` can find the sender.
  const baseUrl = plainShareUrl || (typeof window !== "undefined" ? `${window.location.origin}` : "https://jobgrid.nl");
  const sep = baseUrl.includes("?") ? "&" : "?";
  const shareUrl = `${baseUrl}${sep}src=friend${refCode ? `&ref=${refCode}` : ""}`;
  const message = t("recShareMessage");
  const subject = t("recShareEmailSubject");

  const onShared = () => {
    // Small delayed toast so it doesn't compete with the browser's own
    // "opening new window…" chrome for attention.
    setTimeout(() => toast.success(t("recThankYou")), 400);
  };

  const targets = [
    {
      key: "linkedin",
      icon: Linkedin,
      label: "LinkedIn",
      onClick: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          "share-linkedin", "width=600,height=560,noopener,noreferrer",
        );
        onShared();
      },
    },
    {
      key: "whatsapp",
      icon: MessageCircle,
      label: "WhatsApp",
      onClick: () => {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${message} ${shareUrl}`)}`,
          "share-whatsapp", "noopener,noreferrer",
        );
        onShared();
      },
    },
    {
      key: "twitter",
      icon: Twitter,
      label: "X / Twitter",
      onClick: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}`,
          "share-x", "width=600,height=460,noopener,noreferrer",
        );
        onShared();
      },
    },
    {
      key: "email",
      icon: Mail,
      label: "E-mail",
      onClick: () => {
        // mailto: rich body — line-break becomes `%0A` — the URL renders
        // clickable in every mail client.
        const body = `${message}%0A%0A${shareUrl}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
        onShared();
      },
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("recLinkCopied"));
      setTimeout(() => setCopied(false), 2000);
      onShared();
    } catch {
      toast.error(t("affLinkCopyFailed"));
    }
  };

  const close = () => {
    setStep(initialStep);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={close}
      data-testid="recommend-dialog-backdrop"
    >
      <div
        className="bg-[#F9F8F6] max-w-lg w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="recommend-dialog"
      >
        <button
          type="button"
          data-testid="recommend-close"
          onClick={close}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-neutral-900"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        {step === "choose" && (
          <div data-testid="recommend-choose">
            <p className="ed-eyebrow">{t("recEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-2 leading-tight">{t("recTitle")}</h2>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              {t("recIntro")}
            </p>

            <div className="mt-6 space-y-3">
              {/* Plain-share path */}
              <button
                type="button"
                data-testid="recommend-plain"
                onClick={() => setStep("share")}
                className="w-full text-left flex items-start gap-4 border border-neutral-300 hover:border-neutral-900 bg-white p-4 transition-colors"
              >
                <div className="w-11 h-11 shrink-0 rounded-full bg-neutral-100 text-neutral-900 flex items-center justify-center">
                  <Heart size={17} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg leading-tight">{t("recPlainTitle")}</p>
                  <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
                    {t("recPlainBody")}
                  </p>
                </div>
                <ArrowRight size={14} strokeWidth={1.5} className="mt-4 shrink-0 text-neutral-500" />
              </button>

              {/* Affiliate path — only if not already opted in */}
              {!isAffiliate ? (
                <button
                  type="button"
                  data-testid="recommend-affiliate"
                  onClick={() => { onOptInAffiliate?.(); close(); }}
                  className="w-full text-left flex items-start gap-4 border border-amber-700/40 bg-amber-50/40 hover:border-amber-800 p-4 transition-colors"
                >
                  <div className="w-11 h-11 shrink-0 rounded-full bg-amber-700 text-[#F9F8F6] flex items-center justify-center">
                    <Handshake size={17} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight">{t("recAffiliateTitle")}</p>
                    <p className="text-xs text-neutral-700 mt-1 leading-relaxed">
                      {t("recAffiliateBody")}
                    </p>
                  </div>
                  <ArrowRight size={14} strokeWidth={1.5} className="mt-4 shrink-0 text-neutral-500" />
                </button>
              ) : (
                <div className="border border-emerald-700/30 bg-emerald-50/40 p-4 flex items-start gap-4" data-testid="recommend-affiliate-active">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-emerald-700 text-[#F9F8F6] flex items-center justify-center">
                    <Handshake size={17} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight">{t("recAffiliateActiveTitle")}</p>
                    <p className="text-xs text-neutral-700 mt-1 leading-relaxed">
                      {t("recAffiliateActiveBody")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === "share" && (
          <div data-testid="recommend-share">
            <button
              type="button"
              onClick={() => setStep("choose")}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
            >
              ← {t("recBack")}
            </button>
            <p className="ed-eyebrow mt-3">{t("recShareEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-1 leading-tight">{t("recShareTitle")}</h2>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              {t("recShareIntro")}
            </p>

            {/* Gift promise — the +14 day trial is applied server-side when
                the recipient signs up with ?src=friend on the URL. */}
            <div className="mt-4 flex items-start gap-3 border border-emerald-700/30 bg-emerald-50/40 px-4 py-3" data-testid="recommend-gift-banner">
              <span className="text-lg leading-none">🎁</span>
              <p className="text-sm text-emerald-900 leading-relaxed">
                {t("recGiftPromise")}
              </p>
            </div>

            <div className="mt-4 p-3 bg-white border border-neutral-200 font-mono text-xs break-all text-neutral-700 flex items-center gap-3" data-testid="recommend-share-url">
              <span className="flex-1">{shareUrl}</span>
              <button
                type="button"
                data-testid="recommend-copy"
                onClick={copy}
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-2.5 py-1.5 inline-flex items-center gap-1.5 shrink-0"
              >
                {copied ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.75} />}
                {copied ? t("recCopied") : t("recCopy")}
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {targets.map((tg) => {
                const Icon = tg.icon;
                return (
                  <button
                    key={tg.key}
                    type="button"
                    data-testid={`recommend-share-${tg.key}`}
                    onClick={tg.onClick}
                    className="border border-neutral-300 hover:border-neutral-900 bg-white p-3 flex flex-col items-center gap-2 transition-colors"
                  >
                    <Icon size={18} strokeWidth={1.5} />
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em]">{tg.label}</span>
                  </button>
                );
              })}
            </div>

            {!isAffiliate && (
              <button
                type="button"
                data-testid="recommend-switch-to-affiliate"
                onClick={() => { onOptInAffiliate?.(); close(); }}
                className="mt-5 w-full text-left p-3 bg-amber-50/40 border border-amber-700/30 hover:border-amber-800 font-mono text-[0.65rem] uppercase tracking-[0.12em] text-amber-800 transition-colors inline-flex items-center justify-between gap-2"
              >
                <span>💡 {t("recSwitchToAffiliate")}</span>
                <ArrowRight size={12} strokeWidth={1.75} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
